import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useRouter } from 'next/router'
import cloneDeep from 'lodash.clonedeep'

import { usePagination } from '@hooks/usePagination'
import { useAuthentication } from '@hooks/useAuthentication'
import { useAlert } from '@hooks/useAlert'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { CampaignResponse } from '@models/campaign/CampaignResponse'
import { LikeSuccessStatusEnum } from '@models/book/LikeSuccessStatusEnum'
import { DisplayTypeEnum } from '@interfaces/DisplayTypeEnum'
import { scrollToElement } from '@lib/utils'
import { useBookAction } from '@hooks/book/useBookAction'
import { useCampaignAction } from '@hooks/campaign/useCampaignAction'
import { CampaignSearchFormType } from './components/CampaignSearchForm/interface'
import { CampaignPageProps, WithCampaignPageProps } from './interface'

export function withCampaignPage(Component: React.FC<CampaignPageProps>) {
  function WithCampaignPage({
    campaignId,
    text,
    bookType,
  }: WithCampaignPageProps) {
    const bookClient = useBookAction()
    const campaignClient = useCampaignAction()
    const queryClient = useQueryClient()
    const router = useRouter()
    const alert = useAlert()
    const loginModal = useModal({ modal: 'login' })
    const { isAuthenticated, isUserLoading } = useAuthentication()
    const { page, perpage, pageChange, perpageChange } = usePagination()

    const [searchParam, setSearchParam] = useState<CampaignSearchFormType>({
      searchText: text,
      displayType: DisplayTypeEnum.WEB,
      bookType,
      categoryIds: [],
      penNameIds: [],
    })

    const { data, isLoading } = useQuery(
      ['campaign', page, perpage, searchParam],
      () =>
        campaignClient.getBooksFromBanner(
          campaignId,
          page,
          perpage,
          searchParam
        )
    )

    const { data: bannerName = '', isLoading: bannerNameLoading } = useQuery(
      ['campaign', 'name', campaignId],
      () => campaignClient.getBannerDetail(campaignId),
      { enabled: !!campaignId }
    )

    async function handleSubmit(val: CampaignSearchFormType) {
      if (page !== 1) pageChange(1)

      const query: any = {
        campaignId,
        text: val.searchText,
        advanceSearch: router.query.advanceSearch,
      }

      if (val.bookType) query.bookType = val.bookType
      if (val.categoryIds.length) query.categoryIds = val.categoryIds
      if (val.penNameIds.length) query.penNameIds = val.penNameIds

      router.replace({
        pathname: router.pathname,
        query,
      })
    }

    const { mutate: toggleBookLike, isLoading: isLoadingToggleBookLike } =
      useMutation((id: number) => bookClient.toggleBookLike(id), {
        onSuccess: (type, id) => {
          queryClient.setQueryData<CampaignResponse | undefined>(
            ['campaign', page, perpage, searchParam],
            (oldData?: CampaignResponse) => {
              if (oldData) {
                const dataClone = cloneDeep(oldData)
                const books = dataClone.campains
                const index = books.findIndex(item => item.id === id)
                books[index].isLiked = type === LikeSuccessStatusEnum.CREATE
                books[index].likeCount =
                  type === LikeSuccessStatusEnum.CREATE
                    ? books[index].likeCount + 1
                    : Math.max(0, books[index].likeCount - 1)

                return {
                  ...dataClone,
                  data: books,
                }
              }

              return oldData
            }
          )
        },
      })

    async function handleLikeBook(id: number) {
      if (isAuthenticated) {
        try {
          await toggleBookLike(id)
        } catch (_) {
          alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
        }
      } else {
        loginModal.show()
      }
    }

    function handlePageChange(value: number) {
      pageChange(value)
      scrollToElement('#campaign-list')
    }

    useEffect(() => {
      const categoryIds = router.query.categoryIds
        ? typeof router.query.categoryIds === 'string'
          ? [router.query.categoryIds]
          : router.query.categoryIds
        : []
      const penNameIds = router.query.penNameIds
        ? typeof router.query.penNameIds === 'string'
          ? [router.query.penNameIds]
          : router.query.penNameIds
        : []

      setSearchParam(prev => ({
        ...prev,
        searchText: text,
        bookType,
        categoryIds,
        penNameIds,
      }))
    }, [text, bookType, router.query.categoryIds, router.query.penNameIds])

    const newProps = {
      initialValues: searchParam,
      data: data?.campains || [],
      bannerName,
      page,
      perpage,
      total: data?.total || 0,
      campaignId,
      isLoading: isLoading || isUserLoading || bannerNameLoading,
      isLoadingToggleBookLike,
      handleSubmit,
      handlePageChange,
      perpageChange,
      handleLikeBook,
    }
    return <Component {...newProps} />
  }
  return WithCampaignPage
}
